import React from 'react';
import { View, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, {
  Text as SvgText,
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
} from 'react-native-svg';
import { isIos, isMobile, isNative, isWeb } from '../../utils/responsive';
import { cln } from '../../utils/classnames';
import { AnimatedBirthdayLogo } from '../index';

interface BirthdaySectionProps {
  anniversary: number;
}

const BirthdaySection: React.FC<BirthdaySectionProps> = ({ anniversary }) => {
  const { t } = useTranslation();
  const { colorScheme } = useColorScheme();
  const { i18n } = useTranslation();
  const gradientTextWidthAndroid = i18n.language === 'hu' ? 40 : 53;

  return (
    <View className={cln('mb-10', isNative ? 'px-8' : 'px-0')} style={{ overflow: 'hidden' }}>
      <View
        className={
          isMobile
            ? cln(
                'flex-col w-full items-center flex bg-gradient-to-r from-primary-600 to-secondary-200text-neutral-950 dark:text-neutral-50 py-4',
                isNative ? `${isIos ? 'mt-[102px]' : 'mt-[134px]'} px-4` : 'mt-0 px-6',
              )
            : 'flex-col lg:flex-row items-center justify-between bg-gradient-to-r from-primary-600 to-secondary-200text-neutral-950 dark:text-neutral-50 gap-x-6 px-8 py-4 lg:py-0 mt-0'
        }
      >
        <View className="flex-row items-center pb-3 xs:pb-7 pt-6 overflow-hidden">
          <AnimatedBirthdayLogo />

          <View>
            <Text className="text-neutral-950 dark:text-neutral-50 text-birthdayTitleMobile xs:text-birthdayTitle font-[sans-600] tracking-widest pt-[4px] pb-[2px]">
              {' '}
              {t('birthday:title')}
            </Text>
          </View>
        </View>
        {isNative && (
          <View className="flex-row flex-wrap items-end relative">
            <Text className="flex text-neutral-950 dark:text-neutral-50 font-[sans-400] text-body relative pb-[2px]">
              {t('birthday:joined')} {/* Gradient text is its own inline box */}
              <View
                style={{
                  transform:
                    i18n.language === 'hu'
                      ? `translate(-6px ${isIos ? 16 : 8}px)`
                      : `translate(0px ${isIos ? 16 : 8}px)`,
                  paddingRight: i18n.language === 'hu' ? 0 : 4,
                }}
              >
                {isIos ? (
                  <MaskedView
                    maskElement={
                      <Text style={{ fontSize: 16, lineHeight: 24, fontFamily: 'sans-700' }}>
                        {anniversary} {t('birthday:years')}
                      </Text>
                    }
                  >
                    <LinearGradient
                      colors={
                        colorScheme === 'dark' ? ['#edf115', '#1fffd7'] : ['#623BFF', '#1FFFD7']
                      }
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                    >
                      <Text
                        style={{ fontSize: 16, lineHeight: 24, fontFamily: 'sans-700', opacity: 0 }}
                      >
                        {anniversary} {t('birthday:years')}
                      </Text>
                    </LinearGradient>
                  </MaskedView>
                ) : (
                  <Svg height={24} width={gradientTextWidthAndroid}>
                    <Defs>
                      <SvgLinearGradient
                        id="gradientText"
                        x1="0"
                        y1="0"
                        x2={gradientTextWidthAndroid}
                        y2="0"
                        gradientUnits="userSpaceOnUse"
                      >
                        <Stop
                          offset="0"
                          stopColor={colorScheme === 'dark' ? '#edf115' : '#623BFF'}
                        />
                        <Stop offset="1" stopColor="#1FFFD7" />
                      </SvgLinearGradient>
                    </Defs>
                    <SvgText
                      fill="url(#gradientText)"
                      fontSize={16}
                      fontFamily="sans-700"
                      fontWeight="700"
                      y={16}
                    >
                      {anniversary} {t('birthday:years')}
                    </SvgText>
                  </Svg>
                )}
              </View>
              {t('birthday:thanks')} {t('birthday:being_with_us')}
            </Text>
          </View>
        )}
        {isWeb && (
          <Text>
            <Text className="text-neutral-950 dark:text-neutral-50 font-[sans-400] text-body xs:text-lead">
              {t('birthday:joined')}
            </Text>
            <Text
              key={colorScheme}
              style={{
                background: `linear-gradient(90deg, ${
                  colorScheme === 'dark' ? '#1FFFD7, #EDF115' : '#623BFF, #1FFFD7'
                })`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: 'sans-700',
                fontSize: isMobile ? 16 : 18,
                whiteSpace: 'nowrap',
                marginRight: 5,
                marginLeft: i18n.language === 'hu' ? 0 : 4,
              }}
            >
              {anniversary} {t('birthday:years')}
            </Text>
            <Text className="text-neutral-950 dark:text-neutral-50 font-[sans-400] text-body xs:text-lead">
              {t('birthday:thanks')} {t('birthday:being_with_us')}
            </Text>
          </Text>
        )}
      </View>

      <LinearGradient
        colors={colorScheme === 'dark' ? ['#1fffd7', '#edf115'] : ['#623BFF', '#1FFFD7']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={
          isMobile
            ? {
                height: 1,
                zIndex: 5,
                top: 0,
              }
            : {
                height: 1,
                zIndex: 5,
                top: 0,
              }
        }
      />
    </View>
  );
};

export default BirthdaySection;
